import React, { useState } from 'react';
import propTypes from 'prop-types';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, Table, Collapse, Alert, Progress, Input, Label, FormGroup,
} from 'reactstrap';
import {
  COLUMNS, LIST_SEPARATOR, downloadSampleCsv, parseCsv, rowsToObjects,
  validateRows, runBulk, rollback,
} from '../../../utils/voucherBulk';

const STEP = { PICK: 'pick', PREVIEW: 'preview', RUNNING: 'running', DONE: 'done' };

export default function ModalBulkVoucherUpload(props) {
  const { isOpen, onClose, onCompleted } = props;
  const [step, setStep] = useState(STEP.PICK);
  const [fileName, setFileName] = useState('');
  const [objects, setObjects] = useState([]);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [unknownCols, setUnknownCols] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState([]);
  const [showGuide, setShowGuide] = useState(false);
  const [parseError, setParseError] = useState('');

  const reset = () => {
    setStep(STEP.PICK); setFileName(''); setObjects([]); setErrors([]);
    setWarnings([]); setUnknownCols([]); setProgress({ done: 0, total: 0 });
    setResults([]); setParseError('');
  };

  const close = () => { reset(); onClose(); };

  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) { setParseError('The file must be a .csv'); return; }
    setParseError('');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const matrix = parseCsv(ev.target.result);
        const { objects: objs, unknown } = rowsToObjects(matrix);
        if (!objs.length) { setParseError('No data rows found.'); return; }
        const errs = validateRows(objs);
        const warns = [];
        objs.forEach((o, i) => {
          if (o.voucher_type === 'private' && !o.reg_start && !o.reg_end) {
            warns.push({ row: i + 2, reason: 'private voucher with no registration range reaches nobody until assigned' });
          }
        });
        setObjects(objs); setErrors(errs); setWarnings(warns); setUnknownCols(unknown);
        setStep(STEP.PREVIEW);
      } catch (err) {
        setParseError(`Could not parse the file: ${err.message}`);
      }
    };
    reader.readAsText(file);
  };

  const execute = async () => {
    setStep(STEP.RUNNING);
    setProgress({ done: 0, total: objects.length });
    const res = await runBulk(objects, (done, total) => setProgress({ done, total }));
    setResults(res);
    setStep(STEP.DONE);
    if (onCompleted) onCompleted();
  };

  const doRollback = async () => {
    const n = await rollback(results);
    setResults(results.map((r) => (r.voucherId ? { ...r, status: 'rolled_back' } : r)));
    alert(`Rolled back ${n} created vouchers.`);
  };

  const failed = results.some((r) => r.status === 'failed');
  const createdCount = results.filter((r) => r.voucherId && r.status !== 'rolled_back').length;

  return (
    <Modal isOpen={isOpen} toggle={close} size="lg">
      <ModalHeader toggle={close}>Bulk Upload Vouchers (.csv)</ModalHeader>
      <ModalBody>
        {step === STEP.PICK && (
          <div>
            <p>Download the sample, fill it in Google Sheets, then File &rarr; Download &rarr; Comma-separated values (.csv) and upload it here. Vouchers are created for <strong>Kard</strong> only.</p>
            <Button color="link" className="pl-0" onClick={downloadSampleCsv}>
              <i className="fa fa-download" /> Download sample template (.csv)
            </Button>
            <div className="mt-2">
              <Button color="link" className="pl-0" onClick={() => setShowGuide(!showGuide)}>
                <i className={`fa fa-chevron-${showGuide ? 'down' : 'right'}`} /> Column guide
              </Button>
              <Collapse isOpen={showGuide}>
                <Table size="sm" bordered responsive>
                  <thead><tr><th>Column</th><th>Required</th><th>Fill with</th></tr></thead>
                  <tbody>
                    {COLUMNS.map((c) => (
                      <tr key={c.key}>
                        <td><code>{c.header}</code></td>
                        <td>{c.required ? 'yes' : '-'}</td>
                        <td>{c.help}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <small className="text-muted">Multi-value cells (e.g. sku) separate items with &quot;{LIST_SEPARATOR}&quot;, not a comma.</small>
              </Collapse>
            </div>
            <FormGroup className="mt-3">
              <Label>Upload filled file (.csv)</Label>
              <Input type="file" accept=".csv" onChange={onFile} />
            </FormGroup>
            {parseError && <Alert color="danger">{parseError}</Alert>}
          </div>
        )}

        {step === STEP.PREVIEW && (
          <div>
            <p><strong>{fileName}</strong> &mdash; {objects.length} rows.</p>
            {unknownCols.length > 0 && (
              <Alert color="warning">Unknown columns ignored: {unknownCols.join(', ')}</Alert>
            )}
            {errors.length > 0 ? (
              <Alert color="danger">
                {errors.length} error(s). Nothing will be created until these are fixed.
              </Alert>
            ) : (
              <Alert color="success">All {objects.length} rows valid. Ready to create.</Alert>
            )}
            {errors.length > 0 && (
              <Table size="sm" bordered responsive>
                <thead><tr><th>Row</th><th>Column</th><th>Problem</th></tr></thead>
                <tbody>
                  {errors.slice(0, 200).map((e, i) => (
                    <tr key={i}><td>{e.row}</td><td><code>{e.column}</code></td><td>{e.reason}</td></tr>
                  ))}
                </tbody>
              </Table>
            )}
            {warnings.length > 0 && (
              <Alert color="warning">
                {warnings.map((w, i) => <div key={i}>Row {w.row}: {w.reason}</div>)}
              </Alert>
            )}
          </div>
        )}

        {step === STEP.RUNNING && (
          <div>
            <p>Creating {progress.done} / {progress.total}...</p>
            <Progress value={progress.total ? (progress.done / progress.total) * 100 : 0} />
          </div>
        )}

        {step === STEP.DONE && (
          <div>
            {failed ? (
              <Alert color="danger">Stopped on a failed row. {createdCount} voucher(s) were created before the failure.</Alert>
            ) : (
              <Alert color="success">Done. {createdCount} voucher(s) created.</Alert>
            )}
            <Table size="sm" bordered responsive>
              <thead><tr><th>Row</th><th>Code</th><th>Status</th><th>Notes</th></tr></thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={i}>
                    <td>{r.row}</td><td>{r.code}</td><td>{r.status}</td>
                    <td>{r.error || (r.warnings || []).join('; ')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {step === STEP.PREVIEW && (
          <Button color="primary" disabled={errors.length > 0} onClick={execute}>
            Create {objects.length} vouchers
          </Button>
        )}
        {step === STEP.DONE && failed && createdCount > 0 && (
          <Button color="danger" onClick={doRollback}>Roll back created vouchers</Button>
        )}
        {step === STEP.DONE && (
          <Button color="secondary" onClick={reset}>Upload another</Button>
        )}
        <Button color="link" onClick={close} disabled={step === STEP.RUNNING}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}

ModalBulkVoucherUpload.propTypes = {
  isOpen: propTypes.bool,
  onClose: propTypes.func.isRequired,
  onCompleted: propTypes.func,
};
ModalBulkVoucherUpload.defaultProps = { isOpen: false };
